import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Flex,
  Text,
  Button,
  Card,
  TextField,
  Badge,
  Separator,
} from "@radix-ui/themes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlusIcon, TrashIcon, Cross2Icon } from "@radix-ui/react-icons";
import { JSONSchema7 } from "json-schema";
import Switch from "@/components/shared/inputs/switch/Switch";

import {
  ParameterMapping,
  ParameterMappingEntry,
  ParameterSource,
  EnrichmentMappingContext,
} from "@/v2/entities/enrichment-skill/EnrichmentSkillConnection.model";

interface ParameterMappingInterfaceProps {
  skillParameters?: JSONSchema7;
  currentMapping: ParameterMapping;
  mappingContext: EnrichmentMappingContext;
  onMappingChange: (mapping: ParameterMapping) => void | Promise<void>;
  isLoading?: boolean;
  showSaveButton?: boolean;
}

const SOURCE_OPTIONS = [
  {
    value: "enrichment_output" as ParameterSource,
    label: "Enrichment Output",
    description: "Use data from enrichment processing results",
  },
  {
    value: "enrichment_options" as ParameterSource,
    label: "Enrichment Options",
    description: "Use data from enrichment configuration",
  },
  {
    value: "ticket_id" as ParameterSource,
    label: "Ticket ID",
    description: "Always available ticket identifier",
  },
  {
    value: "conversation_id" as ParameterSource,
    label: "Conversation ID",
    description: "Always available conversation identifier",
  },
  {
    value: "static_value" as ParameterSource,
    label: "Static Value",
    description: "Hardcoded value that never changes",
  },
];

export function ParameterMappingInterface({
  skillParameters,
  currentMapping,
  mappingContext,
  onMappingChange,
  isLoading = false,
  showSaveButton = false,
}: ParameterMappingInterfaceProps) {
  // Get skill parameters from JSON schema (flattened including nested objects)
  const skillParameterOptions = useMemo(() => {
    if (!skillParameters?.properties) return [];

    const flattenParameters = (
      properties: { [key: string]: any },
      required: string[] | undefined,
      prefix = ""
    ): any[] => {
      return Object.entries(properties).flatMap(([key, schema]) => {
        const schemaObj = typeof schema === "object" ? schema : {};
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const isRequired = Array.isArray(required) && required.includes(key);

        // Handle nested objects - recurse if it has properties
        if (schemaObj.type === "object" && schemaObj.properties) {
          return flattenParameters(
            schemaObj.properties,
            schemaObj.required,
            fullKey
          );
        }

        let type = schemaObj.type || "string";

        // Handle array types - check if type is array or if type is array of types
        const isArray =
          type === "array" || (Array.isArray(type) && type.includes("array"));
        const finalType = isArray
          ? "array"
          : Array.isArray(type)
          ? type[0]
          : type;

        return [
          {
            name: fullKey,
            type: finalType,
            isArray,
            description: schemaObj.description || "",
            required: isRequired,
            itemsType:
              isArray &&
              schemaObj.items &&
              typeof schemaObj.items === "object" &&
              "type" in schemaObj.items
                ? (schemaObj.items as { type?: unknown }).type
                : undefined,
          },
        ];
      });
    };

    return flattenParameters(
      skillParameters.properties,
      skillParameters.required as string[]
    );
  }, [skillParameters]);

  // Initialize mappings with proper array and boolean conversion
  const [mappings, setMappings] = useState<ParameterMappingEntry[]>(() => {
    return (currentMapping.mappings || []).map((mapping) => {
      // Use the flattened options to find the parameter
      const param = skillParameterOptions.find(
        (p) => p.name === mapping.skillParameter
      );

      const isArrayParam = param?.isArray || false;
      const isBooleanParam = param?.type === "boolean";

      // Convert comma-separated strings to arrays for array parameters
      if (
        isArrayParam &&
        typeof mapping.value === "string" &&
        mapping.value.trim()
      ) {
        return {
          ...mapping,
          value: mapping.value
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v),
        };
      }

      // Convert string "true"/"false" to boolean for boolean parameters
      if (isBooleanParam && typeof mapping.value === "string") {
        return {
          ...mapping,
          value: mapping.value.toLowerCase() === "true",
        };
      }

      return mapping;
    });
  });

  // Update mappings when currentMapping changes from server
  useEffect(() => {
    const normalizedMappings = (currentMapping.mappings || []).map(
      (mapping) => {
        const param = skillParameterOptions.find(
          (p) => p.name === mapping.skillParameter
        );
        const isArrayParam = param?.isArray || false;
        const isBooleanParam = param?.type === "boolean";

        // Convert comma-separated strings to arrays for array parameters
        if (
          isArrayParam &&
          typeof mapping.value === "string" &&
          mapping.value.trim()
        ) {
          return {
            ...mapping,
            value: mapping.value
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v),
          };
        }

        // Convert string "true"/"false" to boolean for boolean parameters
        if (isBooleanParam && typeof mapping.value === "string") {
          return {
            ...mapping,
            value: mapping.value.toLowerCase() === "true",
          };
        }

        return mapping;
      }
    );

    setMappings(normalizedMappings);
  }, [currentMapping.mappings, skillParameterOptions]);

  // Get available values for a specific source
  const getSourceValues = useMemo(() => {
    return (source: ParameterSource) => {
      switch (source) {
        case "enrichment_output":
          return mappingContext.outputFields.map((field) => ({
            value: field.key,
            label: `${field.name} (${field.type})`,
            description: field.brief,
          }));
        case "enrichment_options":
          return mappingContext.optionFields.map((field) => ({
            value: field.name,
            label: `${field.name} (${field.type})`,
            description: field.description,
          }));
        case "ticket_id":
        case "conversation_id":
          return [
            {
              value: source,
              label: source.replace("_", " ").toUpperCase(),
              description: "",
            },
          ];
        case "static_value":
          return []; // User will input manually
        default:
          return [];
      }
    };
  }, [mappingContext.outputFields, mappingContext.optionFields]);

  // Helper functions for array handling
  const isArrayParameter = useCallback(
    (paramName: string) => {
      const param = skillParameterOptions.find((p) => p.name === paramName);
      return param?.isArray || false;
    },
    [skillParameterOptions]
  );

  // Helper function to check if parameter is boolean type
  const isBooleanParameter = useCallback(
    (paramName: string) => {
      const param = skillParameterOptions.find((p) => p.name === paramName);
      return param?.type === "boolean";
    },
    [skillParameterOptions]
  );

  // Core mapping functions - defined first to avoid hoisting issues
  const handleUpdateMapping = useCallback(
    (index: number, updates: Partial<ParameterMappingEntry>) => {
      setMappings((prev) =>
        prev.map((mapping, i) =>
          i === index ? { ...mapping, ...updates } : mapping
        )
      );
    },
    []
  );

  const handleAddMapping = useCallback(() => {
    setMappings((prev) => [
      ...prev,
      {
        skillParameter: "",
        source: "enrichment_output",
        value: "",
        type: "string",
      },
    ]);
  }, []);

  const handleRemoveMapping = useCallback((index: number) => {
    setMappings((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Helper functions for array handling
  const initializeValue = useCallback(
    (paramName: string) => {
      if (isArrayParameter(paramName)) return [];
      if (isBooleanParameter(paramName)) return false;
      return "";
    },
    [isArrayParameter, isBooleanParameter]
  );

  const addArrayItem = useCallback(
    (mappingIndex: number, item: string) => {
      const mapping = mappings[mappingIndex];
      const currentArray = Array.isArray(mapping.value) ? mapping.value : [];
      handleUpdateMapping(mappingIndex, {
        value: [...currentArray, item],
      });
    },
    [mappings, handleUpdateMapping]
  );

  const removeArrayItem = useCallback(
    (mappingIndex: number, itemIndex: number) => {
      const mapping = mappings[mappingIndex];
      const currentArray = Array.isArray(mapping.value) ? mapping.value : [];
      handleUpdateMapping(mappingIndex, {
        value: currentArray.filter((_, i) => i !== itemIndex),
      });
    },
    [mappings, handleUpdateMapping]
  );

  const updateArrayItem = useCallback(
    (mappingIndex: number, itemIndex: number, newValue: string) => {
      const mapping = mappings[mappingIndex];
      const currentArray = Array.isArray(mapping.value) ? mapping.value : [];
      const updatedArray = [...currentArray];
      updatedArray[itemIndex] = newValue;
      handleUpdateMapping(mappingIndex, { value: updatedArray });
    },
    [mappings, handleUpdateMapping]
  );

  const handleSave = useCallback(async () => {
    const validMappings = mappings.filter((m) => {
      if (!m.skillParameter) return false;
      if (Array.isArray(m.value)) {
        // Arrays can be empty, but if they have items, items shouldn't be empty
        return m.value.every((item) => item.trim() !== "");
      }
      if (typeof m.value === "boolean") {
        // Boolean values are always valid
        return true;
      }
      return m.value && m.value.trim() !== "";
    });

    // Debug: Log mappings to verify arrays are preserved
    console.log(
      "Saving mappings:",
      validMappings.map((m) => ({
        skillParameter: m.skillParameter,
        value: m.value,
        isArray: Array.isArray(m.value),
        type: typeof m.value,
      }))
    );

    await onMappingChange({ mappings: validMappings });
  }, [mappings, onMappingChange]);

  // Use ref to track the last saved state and prevent loops
  const lastSavedMappings = useRef<string>("");
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Auto-save when not showing save button (debounced)
  useEffect(() => {
    if (!showSaveButton) {
      const validMappings = mappings.filter((m) => {
        if (!m.skillParameter) return false;
        if (Array.isArray(m.value)) {
          // Arrays can be empty, but if they have items, items shouldn't be empty
          return m.value.every((item) => item.trim() !== "");
        }
        if (typeof m.value === "boolean") {
          // Boolean values are always valid
          return true;
        }
        return m.value && m.value.trim() !== "";
      });
      const currentMappingsString = JSON.stringify(validMappings);
      const originalMappingsString = JSON.stringify(currentMapping.mappings);

      // Only save if mappings actually changed and different from last saved state
      if (
        currentMappingsString !== originalMappingsString &&
        currentMappingsString !== lastSavedMappings.current
      ) {
        // Clear existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Debounce the save operation
        timeoutRef.current = setTimeout(() => {
          // Debug: Log auto-save mappings to verify arrays are preserved
          console.log(
            "Auto-saving mappings:",
            validMappings.map((m) => ({
              skillParameter: m.skillParameter,
              value: m.value,
              isArray: Array.isArray(m.value),
              type: typeof m.value,
            }))
          );

          lastSavedMappings.current = currentMappingsString;
          onMappingChange({ mappings: validMappings });
        }, 500); // 500ms debounce
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [mappings, showSaveButton, currentMapping.mappings, onMappingChange]);

  if (skillParameterOptions.length === 0) {
    return (
      <Card className="p-4">
        <Text size="2" color="gray">
          This skill has no parameters that require mapping.
        </Text>
      </Card>
    );
  }

  return (
    <Flex direction="column" gap="4">
      <Flex direction="row" align="center" justify="between">
        <Text size="3" weight="medium">
          Parameter Mapping
        </Text>
        <Button size="2" variant="soft" onClick={handleAddMapping}>
          <PlusIcon />
          Add Mapping
        </Button>
      </Flex>

      <Flex direction="column" gap="3">
        {mappings.length === 0 ? (
          <Card className="p-4">
            <Text size="2" color="gray">
              No parameter mappings configured. Add mappings to connect skill
              parameters with enrichment data.
            </Text>
          </Card>
        ) : (
          mappings.map((mapping, index) => (
            <Card key={index} className="p-4">
              <Flex direction="column" gap="3">
                <Flex direction="row" align="center" justify="between">
                  <Text size="2" weight="medium">
                    Mapping {index + 1}
                  </Text>
                  <Button
                    size="1"
                    variant="soft"
                    color="red"
                    onClick={() => handleRemoveMapping(index)}
                  >
                    <TrashIcon />
                  </Button>
                </Flex>

                <Flex direction="row" gap="3" align="end">
                  {/* Skill Parameter Selection */}
                  <Flex direction="column" gap="1" style={{ flex: 1 }}>
                    <Text size="2" color="gray">
                      Skill Parameter
                    </Text>
                    <Select
                      value={mapping.skillParameter}
                      onValueChange={(value) => {
                        const param = skillParameterOptions.find(
                          (p) => p.name === value
                        );
                        handleUpdateMapping(index, {
                          skillParameter: value,
                          type: param?.type || "string",
                          value: initializeValue(value),
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select parameter" />
                      </SelectTrigger>
                      <SelectContent>
                        {skillParameterOptions.map((param) => (
                          <SelectItem key={param.name} value={param.name}>
                            <Flex direction="column">
                              <Flex direction="row" align="center" gap="2">
                                <Text size="2">{param.name}</Text>
                                {param.required && (
                                  <Badge color="red" size="1">
                                    Required
                                  </Badge>
                                )}
                                <Badge
                                  color={param.isArray ? "blue" : "gray"}
                                  size="1"
                                >
                                  {param.isArray
                                    ? `${param.type}[]`
                                    : param.type}
                                </Badge>
                              </Flex>
                              {param.description && (
                                <Text size="1" color="gray">
                                  {param.description}
                                </Text>
                              )}
                            </Flex>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Flex>

                  {/* Source Selection */}
                  <Flex direction="column" gap="1" style={{ flex: 1 }}>
                    <Text size="2" color="gray">
                      Data Source
                    </Text>
                    <Select
                      value={mapping.source}
                      onValueChange={(value: ParameterSource) =>
                        handleUpdateMapping(index, {
                          source: value,
                          value: initializeValue(mapping.skillParameter),
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {SOURCE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <Flex direction="column">
                              <Text size="2">{option.label}</Text>
                              <Text size="1" color="gray">
                                {option.description}
                              </Text>
                            </Flex>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Flex>

                  {/* Value Selection/Input */}
                  <Flex direction="column" gap="1" style={{ flex: 1 }}>
                    <Text size="2" color="gray">
                      Value
                    </Text>
                    {isArrayParameter(mapping.skillParameter) ? (
                      // Array parameter handling
                      <Flex direction="column" gap="2">
                        {/* Show array type badge */}
                        <Badge color="blue" size="1">
                          {`Array of ${
                            skillParameterOptions.find(
                              (p) => p.name === mapping.skillParameter
                            )?.itemsType ?? "string"
                          }`}
                        </Badge>

                        {/* Current array items */}
                        {Array.isArray(mapping.value) &&
                          mapping.value.length > 0 && (
                            <Flex direction="column" gap="2">
                              {mapping.value.map(
                                (item: string, itemIndex: number) => (
                                  <Flex key={itemIndex} align="center" gap="2">
                                    <Text
                                      size="1"
                                      color="gray"
                                      style={{ minWidth: "20px" }}
                                    >
                                      {itemIndex + 1}.
                                    </Text>
                                    <TextField.Root
                                      style={{ flex: 1 }}
                                      value={item}
                                      onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                      ) =>
                                        updateArrayItem(
                                          index,
                                          itemIndex,
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter value"
                                    />
                                    <Button
                                      size="1"
                                      variant="ghost"
                                      color="red"
                                      onClick={() =>
                                        removeArrayItem(index, itemIndex)
                                      }
                                    >
                                      <Cross2Icon />
                                    </Button>
                                  </Flex>
                                )
                              )}
                            </Flex>
                          )}

                        {/* Add new item section */}
                        {mapping.source === "static_value" ? (
                          <Button
                            size="2"
                            variant="soft"
                            onClick={() => addArrayItem(index, "")}
                          >
                            <PlusIcon />
                            Add Item
                          </Button>
                        ) : (
                          <Flex direction="column" gap="2">
                            <Text size="1" color="gray">
                              Select values to add to array:
                            </Text>
                            <Select
                              value=""
                              onValueChange={(value) => {
                                if (value) {
                                  addArrayItem(index, value);
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select value to add" />
                              </SelectTrigger>
                              <SelectContent>
                                {getSourceValues(mapping.source).map(
                                  (option) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      <Flex direction="column">
                                        <Text size="2">{option.label}</Text>
                                        {option.description && (
                                          <Text size="1" color="gray">
                                            {option.description}
                                          </Text>
                                        )}
                                      </Flex>
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                          </Flex>
                        )}
                      </Flex>
                    ) : // Single value parameter handling
                    isBooleanParameter(mapping.skillParameter) ? (
                      // Boolean parameter handling
                      <Flex direction="column" gap="2">
                        <Badge color="blue" size="1">
                          Boolean
                        </Badge>
                        {mapping.source === "static_value" ? (
                          <Flex align="center" gap="2">
                            <Switch
                              value={typeof mapping.value === "boolean" ? mapping.value : false}
                              onChange={(value) =>
                                handleUpdateMapping(index, { value })
                              }
                            />
                            <Text size="2" color="gray">
                              {typeof mapping.value === "boolean" && mapping.value ? "True" : "False"}
                            </Text>
                          </Flex>
                        ) : (
                          <Select
                            value={
                              typeof mapping.value === "string" ? mapping.value : ""
                            }
                            onValueChange={(value) =>
                              handleUpdateMapping(index, { value })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select boolean value" />
                            </SelectTrigger>
                            <SelectContent>
                              {getSourceValues(mapping.source).map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  <Flex direction="column">
                                    <Text size="2">{option.label}</Text>
                                    {option.description && (
                                      <Text size="1" color="gray">
                                        {option.description}
                                      </Text>
                                    )}
                                  </Flex>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </Flex>
                    ) : mapping.source === "static_value" ? (
                      <TextField.Root
                        value={
                          typeof mapping.value === "string" ? mapping.value : ""
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleUpdateMapping(index, { value: e.target.value })
                        }
                        placeholder="Enter static value"
                      />
                    ) : (
                      <Select
                        value={
                          typeof mapping.value === "string" ? mapping.value : ""
                        }
                        onValueChange={(value) =>
                          handleUpdateMapping(index, { value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {getSourceValues(mapping.source).map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              <Flex direction="column">
                                <Text size="2">{option.label}</Text>
                                {option.description && (
                                  <Text size="1" color="gray">
                                    {option.description}
                                  </Text>
                                )}
                              </Flex>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          ))
        )}
      </Flex>

      {/* Required Parameters Warning */}
      {skillParameterOptions.some((p) => p.required) && (
        <>
          <Separator />
          <Card className="p-3" style={{ backgroundColor: "var(--yellow-2)" }}>
            <Text size="2" color="orange" weight="medium">
              Required Parameters:
            </Text>
            <Flex direction="row" gap="2" wrap="wrap" className="mt-2">
              {skillParameterOptions
                .filter((p) => p.required)
                .map((param) => (
                  <Badge key={param.name} color="orange" size="1">
                    {param.name} ({param.type})
                  </Badge>
                ))}
            </Flex>
          </Card>
        </>
      )}

      {/* Available Data Preview */}
      <Separator />
      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          Available Data Sources:
        </Text>
        <Flex direction="row" gap="4" wrap="wrap">
          <Card className="p-2">
            <Text size="1" weight="medium">
              Enrichment Output:
            </Text>
            <Flex direction="column" gap="1" className="mt-1">
              {mappingContext.outputFields.slice(0, 3).map((field) => (
                <Text key={field.name} size="1" color="gray">
                  {field.name} ({field.type})
                </Text>
              ))}
              {mappingContext.outputFields.length > 3 && (
                <Text size="1" color="gray">
                  +{mappingContext.outputFields.length - 3} more...
                </Text>
              )}
            </Flex>
          </Card>

          <Card className="p-2">
            <Text size="1" weight="medium">
              Enrichment Options:
            </Text>
            <Flex direction="column" gap="1" className="mt-1">
              {mappingContext.optionFields.slice(0, 3).map((field) => (
                <Text key={field.name} size="1" color="gray">
                  {field.name} ({field.type})
                </Text>
              ))}
              {mappingContext.optionFields.length > 3 && (
                <Text size="1" color="gray">
                  +{mappingContext.optionFields.length - 3} more...
                </Text>
              )}
            </Flex>
          </Card>

          <Card className="p-2">
            <Text size="1" weight="medium">
              Always Available:
            </Text>
            <Flex direction="column" gap="1" className="mt-1">
              <Text size="1" color="gray">
                ticket_id (string)
              </Text>
              <Text size="1" color="gray">
                conversation_id (string)
              </Text>
            </Flex>
          </Card>
        </Flex>
      </Flex>

      {showSaveButton && (
        <>
          <Separator />
          <Flex justify="end">
            <Button
              onClick={handleSave}
              disabled={isLoading || mappings.length === 0}
            >
              {isLoading ? "Saving..." : "Save Connection"}
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  );
}
